import { useForm } from 'react-hook-form';
import { ReactNode } from 'react';
import { E164Number } from 'libphonenumber-js';
import getCountryCode from 'utils/getCountryCode';
import { useSelector } from 'react-redux';
import { AddressDetailsSelector, AddressSelector } from 'store/selectors';
import { zodResolver } from '@hookform/resolvers/zod';
import FormInputItem from 'ui/molecules/FormInputItem';
import { useTranslation } from 'react-i18next';
import FormPhoneItem from 'ui/molecules/FormPhoneItem';
import { Box, Text } from '@chakra-ui/react';
import useFormContactSchema, {
  FormContactSchema,
} from './hooks/useFormContactSchema';

type Props = {
  submitElement: ReactNode;
  onSubmit: (data: FormContactSchema) => Promise<void>;
  isSubmitting: boolean;
};

export const ContactForm = ({ onSubmit, ...props }: Props) => {
  const { t } = useTranslation();
  const address = useSelector(AddressSelector);
  const addressDetails = useSelector(AddressDetailsSelector);
  const schema = useFormContactSchema();

  const { register, handleSubmit, formState, control, setValue } =
    useForm<FormContactSchema>({
      resolver: zodResolver(schema),
      shouldFocusError: true,
    });
  const { errors } = formState;
  return (
    <form
      style={
        props.isSubmitting
          ? {
              pointerEvents: 'none',
              opacity: '0.9',
              userSelect: 'none',
            }
          : {}
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.firstName')}
        errorMessage={errors.firstName?.message}
        {...register('firstName', {
          required: true,
        })}
        required
      />

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.lastName')}
        errorMessage={errors.lastName?.message}
        {...register('lastName', {
          required: true,
        })}
        required
      />

      <Box padding="10px 0px" w="full">
        <Text>{t('form.contact.address')}</Text>
        <Text fontWeight="bold">{address}</Text>
      </Box>

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.email')}
        errorMessage={errors.email?.message}
        {...register('email', {
          required: true,
        })}
        required
      />

      <FormPhoneItem
        label={t('form.contact.phone')}
        errorMessage={errors.phone?.message}
        formControl={control}
        onChangeCallback={(value: E164Number) => {
          setValue('phone', value, { shouldValidate: true });
        }}
        defaultCountry={getCountryCode(addressDetails.countryCode || 'BE')}
        {...register('phone', {
          required: true,
        })}
        required
      />

      <Text py={4} color="primaryColor">
        {t('form.mandatory.label')}
      </Text>

      {props.submitElement}
    </form>
  );
};
export default ContactForm;
